import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { userAPI } from '../../../services';
import {
  Button,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  Badge,
} from '../../UI';
import {
  User,
  Calendar,
  Mail,
  MapPin,
  FileText,
  Save,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Home,
  Heart,
  Phone,
  Users,
} from 'lucide-react';

const MyProfilePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [personal, setPersonal] = useState(null);
  const [pendingPersonal, setPendingPersonal] = useState(null);

  const [form, setForm] = useState({
    dateOfBirth: '',
    fatherName: '',
    pan: '',
    differentlyAbled: '',
    personalEmail: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  useEffect(() => {
    fetchPersonalDetails();
  }, []);

  const fetchPersonalDetails = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getMyPersonalDetails();
      if (response.status === 'success') {
        const { personal: p, pendingPersonal: pp } = response.data;
        setPersonal(p);
        setPendingPersonal(pp);

        // Pre-fill form with existing personal data
        if (p) {
          setForm({
            dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth).toISOString().split('T')[0] : '',
            fatherName: p.fatherName || '',
            pan: p.pan || '',
            differentlyAbled: p.differentlyAbled || '',
            personalEmail: p.personalEmail || '',
            address: {
              line1: p.address?.line1 || '',
              line2: p.address?.line2 || '',
              city: p.address?.city || '',
              state: p.address?.state || '',
              pincode: p.address?.pincode || ''
            },
            emergencyContact: {
              name: p.emergencyContact?.name || '',
              relationship: p.emergencyContact?.relationship || '',
              phone: p.emergencyContact?.phone || ''
            }
          });
        }
      }
    } catch (err) {
      setError('Failed to load personal details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await userAPI.submitPersonalDetails(form);
      if (response.status === 'success') {
        setSuccess('Personal details submitted for admin approval!');
        setPendingPersonal(response.data.pendingPersonal);
        setTimeout(() => setSuccess(''), 4000);
      }
    } catch (err) {
      setError(err.message || 'Failed to submit personal details');
    } finally {
      setSaving(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const isPending = pendingPersonal?.status === 'pending';
  const isRejected = pendingPersonal?.status === 'rejected';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <User className="w-6 h-6" />
          My Profile - Personal Details
        </h2>
        <p className="text-gray-500 mt-1">
          Fill in your personal details. They will be sent to your admin for approval.
        </p>
      </div>

      {/* Status Banner */}
      {isPending && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Pending Approval</p>
            <p className="text-sm text-yellow-700">
              Your personal details were submitted on {new Date(pendingPersonal.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} and are awaiting admin approval.
            </p>
          </div>
        </div>
      )}

      {isRejected && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Rejected</p>
            <p className="text-sm text-red-700">
              Your submission was rejected{pendingPersonal.rejectionReason ? `: ${pendingPersonal.rejectionReason}` : '.'}
              {' '}You can re-submit with corrected details.
            </p>
          </div>
        </div>
      )}

      {pendingPersonal?.status === 'approved' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Approved</p>
            <p className="text-sm text-green-700">Your personal details have been approved by admin.</p>
          </div>
        </div>
      )}

      {/* Employee Info Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Basic Info (read-only)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Name</p>
            <p className="font-medium">{user?.name || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Employee ID</p>
            <p className="font-medium">{user?.employeeId || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Department</p>
            <p className="font-medium">{user?.department || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Designation</p>
            <p className="font-medium">{user?.designation || '-'}</p>
          </div>
        </div>
      </div>

      {/* Personal Details Form */}
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Personal Details</h3>
              <p className="text-sm text-gray-500">Fill in your personal information below</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
              <input
                type="text"
                value={calculateAge(form.dateOfBirth) || ''}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                disabled
                placeholder="Auto-calculated"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Father's Name
              </label>
              <input
                type="text"
                value={form.fatherName}
                onChange={(e) => setForm({ ...form, fatherName: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Father's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PAN</label>
              <input
                type="text"
                value={form.pan}
                onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="AAAAA0000A"
                maxLength={10}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Differently Abled</label>
              <select
                value={form.differentlyAbled}
                onChange={(e) => setForm({ ...form, differentlyAbled: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Not Applicable</option>
                <option value="visual">Visual Impairment</option>
                <option value="hearing">Hearing Impairment</option>
                <option value="locomotor">Locomotor Disability</option>
                <option value="intellectual">Intellectual Disability</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Personal Email</label>
              <input
                type="email"
                value={form.personalEmail}
                onChange={(e) => setForm({ ...form, personalEmail: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="personal@email.com"
              />
            </div>
          </div>

          {/* Address */}
          <div className="mt-8">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Residential Address
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1</label>
                <input
                  type="text"
                  value={form.address.line1}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, line1: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="House/Flat No., Building Name"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2</label>
                <input
                  type="text"
                  value={form.address.line2}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, line2: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Street, Area, Landmark"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={form.address.city}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="City"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  value={form.address.state}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, state: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="State"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">PIN Code</label>
                <input
                  type="text"
                  value={form.address.pincode}
                  onChange={(e) => setForm({ ...form, address: { ...form.address, pincode: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="mt-8">
            <h4 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Emergency Contact
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                <input
                  type="text"
                  value={form.emergencyContact.name}
                  onChange={(e) => setForm({ ...form, emergencyContact: { ...form.emergencyContact, name: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Emergency contact name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                <select
                  value={form.emergencyContact.relationship}
                  onChange={(e) => setForm({ ...form, emergencyContact: { ...form.emergencyContact, relationship: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Relationship</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Parent">Parent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Child">Child</option>
                  <option value="Friend">Friend</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={form.emergencyContact.phone}
                  onChange={(e) => setForm({ ...form, emergencyContact: { ...form.emergencyContact, phone: e.target.value } })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
            {isPending ? (
              <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 px-4 py-2 rounded-lg">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Awaiting admin approval...</span>
              </div>
            ) : (
              <Button type="submit" loading={saving} className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Submit for Approval
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default MyProfilePage;
